import type { HTMLAttributes, ReactNode, ThHTMLAttributes, TdHTMLAttributes } from 'react';
import { cn } from '@/lib/cn';

/**
 * Wide tables scroll inside their own container — the page body never scrolls
 * horizontally.
 */
export function TableWrap({ className, children }: { className?: string; children: ReactNode }) {
  return <div className={cn('w-full overflow-x-auto', className)}>{children}</div>;
}

export function Table({ className, children, ...props }: HTMLAttributes<HTMLTableElement>) {
  return (
    <table className={cn('w-full min-w-max border-collapse text-sm', className)} {...props}>
      {children}
    </table>
  );
}

export function THead({ children }: { children: ReactNode }) {
  return <thead className="bg-surface-2">{children}</thead>;
}

export function TBody({ children }: { children: ReactNode }) {
  return <tbody>{children}</tbody>;
}

interface TRProps extends HTMLAttributes<HTMLTableRowElement> {
  interactive?: boolean;
}

export function TR({ interactive, className, children, ...props }: TRProps) {
  return (
    <tr
      className={cn(
        'border-b border-line last:border-0',
        interactive && 'cursor-pointer transition-colors hover:bg-surface-2',
        className
      )}
      {...props}
    >
      {children}
    </tr>
  );
}

interface CellProps {
  align?: 'left' | 'right' | 'center';
}

const ALIGN = { left: 'text-left', right: 'text-right', center: 'text-center' } as const;

export function TH({
  align = 'left',
  className,
  children,
  ...props
}: ThHTMLAttributes<HTMLTableCellElement> & CellProps) {
  return (
    <th
      scope="col"
      className={cn(
        'px-4 py-2.5 text-xs font-medium tracking-wide whitespace-nowrap text-fg-subtle',
        ALIGN[align],
        className
      )}
      {...props}
    >
      {children}
    </th>
  );
}

export function TD({
  align = 'left',
  className,
  children,
  ...props
}: TdHTMLAttributes<HTMLTableCellElement> & CellProps) {
  return (
    <td className={cn('px-4 py-2.5 whitespace-nowrap', ALIGN[align], className)} {...props}>
      {children}
    </td>
  );
}

/** Full-width row for empty / error / loading messages inside a table body. */
export function TableMessage({ colSpan, children }: { colSpan: number; children: ReactNode }) {
  return (
    <tr>
      <td colSpan={colSpan} className="p-0">
        {children}
      </td>
    </tr>
  );
}
